
import { Component, Vue, Prop, Emit } from "vue-property-decorator";
import {
	CustomFieldExtendedDataTypes,
	CustomFieldExtendedDataSupportedTypes,
	CustomFieldDto,
	CustomFieldExtendedData,
	CustomFieldExtendedDataEmitValue
} from "@/store/custom-fields/types";
import CustomDefinedFieldExtendedDataActionPlanResult from "@/components/custom-fields/extended-data/ActionPlanResult.vue";

/**
 * CustomDefinedFieldExtendedData.vue
 *
 * This component renders a custom field input component based on the provided extendedDataType.
 * It displays an icon button to trigger the extended component and emits input events with updated values.
 */

type CallbackFn = (value: any) => string | Promise<string>;

@Component({
	components: {
		CustomDefinedFieldExtendedDataActionPlanResult,
	},
})

export default class CustomDefinedFieldExtendedData extends Vue {
	@Prop({ required: true }) public customField!: CustomFieldDto;

	/**
	 * Optional callback function to be called after input.
	 */
	@Prop({ required: false }) public callback?: CallbackFn;

	@Prop({ type: Array, default: () => [] }) public valuesInUse!: string[];
	
	/**
	 * Optional parameters to pass to the extended component.
	 */
	@Prop({ required: false }) public data?: any;


	private extendedDataType: CustomFieldExtendedDataTypes | null = null;

	private extraData: CustomFieldExtendedData = {
		// Additional data that can be passed to the extended component.
		customField: null,
		data: null,
	};
	
	protected currentComponent: string | null = null;

	private async mounted(): Promise<void> {
		this.currentComponent = null;

		await this.loadComponent(this.customField);
	}

	private destroyed(): void {
		this.currentComponent = null;
	}

	/**
	 * Dynamically loads the component based on the custom field type and supported options.
	 * @param customField The custom field data to determine the component to load.
	 */
	private async loadComponent(customField: CustomFieldDto): Promise<void> {
		// include the custom field in the data object for the extended component.
		this.extraData = {
			data: this.data,
			customField: customField,
		};

		// check if the custom field type is supported for extended data.
		if (CustomFieldExtendedDataSupportedTypes.includes(customField.customFieldTypeId)) {
			if (customField.options.dataFromActionPlans) {
				this.extendedDataType = CustomFieldExtendedDataTypes.ActionPlanResult;
				this.currentComponent = this.getComponentName;
			}
		}
	}

	private get getComponentName(): string {
		return `CustomDefinedFieldExtendedData${this.extendedDataType}`;
	}

	private async onInput(value: CustomFieldExtendedDataEmitValue): Promise<void> {
		this.$emit("input", value);
		if (this.callback) {
			await this.callback(value);
		}
	}

	@Emit("show-action-plan-alert")
	private onActionPlanResult(value: boolean): boolean {
		return value;
	}
}
